import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Info } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (d) => {
    if (!d) return "—";
    const s = String(d);
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return s;
};

const BUCKET_LABELS = {
    all: "All rows",
    approved: "Auto-approved",
    queued: "Queued (added to verification queue)",
    already_queued: "Already in queue (UTR was queued before)",
    already_verified: "Already verified (slip existed & was verified)",
    unparseable: "Bad rows (couldn't parse)",
};
const BUCKET_TONES = {
    all: "text-slate-800",
    approved: "text-emerald-800",
    queued: "text-amber-800",
    already_queued: "text-slate-700",
    already_verified: "text-blue-800",
    unparseable: "text-rose-800",
};

/**
 * v1.24.14 — Dialog that shows the row-level detail for one bucket of one
 * bank-upload batch. Every count on the "Recent Bank Uploads" panel is
 * clickable and opens this dialog.
 */
export default function BatchRowsDialog({ open, onOpenChange, batchId, bucket, batchMeta }) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!open || !batchId || !bucket) return;
        setLoading(true);
        setData(null);
        api.get(`/admin/bank-upload-batches/${batchId}/rows/${bucket}`)
            .then((r) => setData(r.data))
            .catch(() => setData({ detail_available: false, message: "Failed to load", legacy: true }))
            .finally(() => setLoading(false));
    }, [open, batchId, bucket]);

    const rows = data?.rows || [];
    const tone = BUCKET_TONES[bucket] || "text-slate-800";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl" data-testid="batch-rows-dialog">
                <DialogHeader>
                    <DialogTitle className={`font-display ${tone}`}>
                        {BUCKET_LABELS[bucket] || bucket} · <span className="tabular-nums">{data?.count ?? "…"}</span>
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500">
                        Batch: <span className="font-mono">{batchMeta?.filename || batchId?.slice(0, 8)}</span>
                    </DialogDescription>
                </DialogHeader>

                {loading && <div className="p-8 text-center text-sm text-slate-500">Loading…</div>}

                {!loading && data && !data.detail_available && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                        <div className="flex items-start gap-2">
                            <Info size={16} weight="fill" className="mt-0.5 shrink-0 text-amber-700" />
                            <div>
                                <div className="font-semibold">Detail not saved for this bucket</div>
                                <div className="mt-1 text-[12px] text-amber-800/80">{data.message}</div>
                                <div className="mt-2 text-[11px] text-amber-800/80">Count: <span className="font-mono font-bold">{data.count}</span></div>
                            </div>
                        </div>
                    </div>
                )}

                {!loading && data?.detail_available && rows.length === 0 && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">
                        No rows in this bucket.
                    </div>
                )}

                {!loading && data?.detail_available && rows.length > 0 && (
                    <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-slate-200">
                        <table className="min-w-full text-xs">
                            <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
                                <tr>
                                    <th className="px-3 py-2 text-left">Row #</th>
                                    <th className="px-3 py-2 text-left">Date · Time</th>
                                    <th className="px-3 py-2 text-left">UTR</th>
                                    <th className="px-3 py-2 text-right">Amount</th>
                                    {bucket === "approved" && <th className="px-3 py-2 text-left">Party (verified)</th>}
                                    {bucket === "already_verified" && <>
                                        <th className="px-3 py-2 text-left">Party</th>
                                        <th className="px-3 py-2 text-right">Verified Amount</th>
                                    </>}
                                    {bucket === "already_queued" && (
                                        <th className="px-3 py-2 text-right">Queued Amount</th>
                                    )}
                                    {bucket === "unparseable" && (
                                        <th className="px-3 py-2 text-left">Raw cells</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {rows.map((r, i) => (
                                    <tr key={i} className="hover:bg-slate-50/60">
                                        <td className="px-3 py-1.5 text-slate-500 tabular-nums">{r.row ?? i + 1}</td>
                                        <td className="px-3 py-1.5">
                                            <div className="text-slate-800">{fmtDate(r.date)}</div>
                                            {r.time && <div className="text-[10px] text-slate-500">{r.time}</div>}
                                        </td>
                                        <td className="px-3 py-1.5 font-mono text-slate-700">{r.utr || "—"}</td>
                                        <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-slate-900">{r.amount != null ? fmt(r.amount) : "—"}</td>
                                        {bucket === "approved" && <td className="px-3 py-1.5 text-slate-800">{r.party_name || "—"}</td>}
                                        {bucket === "already_verified" && <>
                                            <td className="px-3 py-1.5 text-slate-800">{r.party_name || "—"}</td>
                                            <td className="px-3 py-1.5 text-right tabular-nums text-blue-800">{r.verified_amount != null ? fmt(r.verified_amount) : "—"}</td>
                                        </>}
                                        {bucket === "already_queued" && (
                                            <td className="px-3 py-1.5 text-right tabular-nums text-amber-800">{r.queued_amount != null ? fmt(r.queued_amount) : "—"}</td>
                                        )}
                                        {bucket === "unparseable" && (
                                            <td className="px-3 py-1.5 font-mono text-[10px] text-rose-700">{(r.raw || []).join(" · ")}</td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
